/**
 * DataFeed API Service
 *
 * Purpose: Fetch historical data and percentile bands from DataFeed.Server
 * Base URL: http://localhost:5100/api
 * Swagger: http://localhost:5100/swagger/index.html
 *
 * @module api/dataFeedApi
 */

const DATAFEED_BASE_URL = process.env.REACT_APP_DATAFEED_API_URL || 'http://localhost:5100/api';

/**
 * Fetch strategy historical data with percentile bands
 *
 * This endpoint returns:
 * - Historical bars (spread, beta, zScore, session)
 * - Percentile bands (P1, P5, P10, P25, P50, P75, P90, P95, P99)
 * - Daily high/low
 * - Volatility regime (NORMAL, HIGH, EXTREME)
 *
 * Usage Pattern:
 * 1. On card mount: Fetch 100 bars with percentiles (once)
 * 2. Every 15 minutes: Re-fetch percentiles only (lightweight)
 * 3. Real-time updates: Append SignalR ticks to historical bars
 *
 * @param {string} symbol - Trading pair (e.g., "XAUUSD/XAUEUR")
 * @param {string} timeframe - Timeframe: "M1", "M15", "H1", "H4", "D1" (default: "H1")
 * @param {number} bars - Number of bars to fetch (1-10000, default: 100)
 * @param {boolean} includePercentiles - Include percentile bands (default: true)
 * @param {boolean} includeZScore - Include Z-Score values (default: true)
 * @param {boolean} includeSpreadOnly - Minimal payload mode (default: false)
 * @returns {Promise<Object>} Response with data array and percentiles object
 *
 * @example
 * // Initial load (100 bars + percentiles)
 * const response = await fetchStrategyData("XAUUSD/XAUEUR", "H1", 100, true);
 * setHistoricalBars(response.data);
 * setPercentileBands(response.percentiles);
 *
 * @example
 * // Lightweight refresh (percentiles only, 15-min interval)
 * const response = await fetchStrategyData("XAUUSD/XAUEUR", "H1", 1, true, false, true);
 * setPercentileBands(response.percentiles);
 */
export const fetchStrategyData = async (
    symbol,
    timeframe = 'H1',
    bars = 100,
    includePercentiles = true,
    includeZScore = true,
    includeSpreadOnly = false
) => {
    // Input validation
    if (!symbol || typeof symbol !== 'string') {
        throw new Error('Invalid symbol parameter');
    }

    // Build query string
    const params = new URLSearchParams({
        symbol: symbol,
        timeframe: timeframe,
        bars: bars.toString(),
        includePercentiles: includePercentiles.toString(),
        includeZScore: includeZScore.toString(),
        includeSpreadOnly: includeSpreadOnly.toString()
    });

    const url = `${DATAFEED_BASE_URL}/betaspread/strategy-data?${params.toString()}`;

    try {
        console.log(`[DataFeed API] Fetching strategy data: ${symbol} ${timeframe} ${bars} bars`);

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error(`Symbol not found: ${symbol}`);
            }
            throw new Error(`DataFeed API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        // Validate response structure
        if (!data || typeof data !== 'object') {
            throw new Error('Invalid response format from DataFeed API');
        }

        if (!Array.isArray(data.data)) {
            console.warn('[DataFeed API] Response missing data array', data);
            data.data = [];
        }

        // Log percentile bands for debugging
        if (includePercentiles && data.percentiles) {
            console.log('[DataFeed API] Percentile bands:', {
                P25: data.percentiles.p25,
                P50: data.percentiles.p50,
                P75: data.percentiles.p75,
                regime: data.percentiles.volatilityRegime,
                session: data.percentiles.session
            });
        }

        console.log(`[DataFeed API] Received ${data.count} bars for ${symbol}`);

        return data;

    } catch (error) {
        console.error('[DataFeed API] Fetch failed:', error.message);
        throw error;
    }
};

/**
 * Fetch latest spread value (lightweight endpoint)
 *
 * Use this for quick updates without fetching full historical data.
 *
 * @param {string} symbol - Trading pair (e.g., "XAUUSD/XAUEUR")
 * @returns {Promise<Object>} Latest spread data
 *
 * @example
 * const latest = await fetchLatestSpread("XAUUSD/XAUEUR");
 * console.log(latest.spread); // 2505.23
 */
export const fetchLatestSpread = async (symbol) => {
    if (!symbol || typeof symbol !== 'string') {
        throw new Error('Invalid symbol parameter');
    }

    const url = `${DATAFEED_BASE_URL}/betaspread/latest?symbol=${encodeURIComponent(symbol)}`;

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`DataFeed API error: ${response.status}`);
        }

        return await response.json();

    } catch (error) {
        console.error('[DataFeed API] fetchLatestSpread failed:', error.message);
        throw error;
    }
};

/**
 * Fetch strategy historical data by individual symbols (NEW ENDPOINT)
 *
 * This is the preferred endpoint that accepts individual symbol names
 * instead of a pre-constructed pair name. The backend resolves the correct
 * pair and returns data + dailyStats.
 *
 * Benefits:
 * - Avoids "Symbol not found" errors from pair name mismatches
 * - Returns dailyStats object with actual daily high/low for each indicator
 * - Backend automatically matches symbols to registered pairs
 *
 * @param {string} symbol1 - First symbol (e.g., "XAUUSD_ct")
 * @param {string} symbol2 - Second symbol (e.g., "XAUEUR_ct")
 * @param {string} timeframe - Timeframe: "M1", "M15", "H1", "H4", "D1" (default: "H1")
 * @param {number} bars - Number of bars to fetch (1-10000, default: 100)
 * @returns {Promise<Object>} Response with data array, percentiles, and dailyStats
 *
 * @example
 * // Initial load with dailyStats
 * const response = await fetchStrategyDataBySymbols("XAUUSD_ct", "XAUEUR_ct", "H1", 100);
 * setHistoricalBars(response.data);
 * setPercentileBands(response.percentiles);
 * setDailyStats(response.dailyStats);  // NEW: spreadHigh, spreadLow, betaHigh, betaLow, zScoreHigh, zScoreLow
 */
export const fetchStrategyDataBySymbols = async (
    symbol1,
    symbol2,
    timeframe = 'H1',
    bars = 100
) => {
    // Input validation
    if (!symbol1 || typeof symbol1 !== 'string') {
        throw new Error('Invalid symbol1 parameter');
    }
    if (!symbol2 || typeof symbol2 !== 'string') {
        throw new Error('Invalid symbol2 parameter');
    }

    // Build query string
    const params = new URLSearchParams({
        symbol1: symbol1,
        symbol2: symbol2,
        timeframe: timeframe,
        bars: bars.toString()
    });

    const url = `${DATAFEED_BASE_URL}/betaspread/data-by-symbols?${params.toString()}`;

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error(`Symbols not found: ${symbol1} + ${symbol2}`);
            }
            throw new Error(`DataFeed API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        // Validate response structure
        if (!data || typeof data !== 'object') {
            throw new Error('Invalid response format from DataFeed API');
        }

        if (!Array.isArray(data.data)) {
            console.warn('[DataFeed API] Response missing data array', data);
            data.data = [];
        }

        return data;

    } catch (error) {
        console.error('[DataFeed API] fetchStrategyDataBySymbols failed:', error.message);
        throw error;
    }
};

/**
 * Fetch only percentile bands (lightweight, fast refresh)
 *
 * Use this for 15-minute refresh cycles to update percentile reference lines
 * without re-fetching full historical data.
 *
 * @param {string} symbol - Trading pair (e.g., "XAUUSD/XAUEUR")
 * @param {string} timeframe - Timeframe (default: "H1")
 * @returns {Promise<Object>} Percentile bands object
 *
 * @example
 * // Every 15 minutes
 * const percentiles = await fetchPercentileBands("XAUUSD/XAUEUR", "H1");
 * setPercentileBands(percentiles);
 */
export const fetchPercentileBands = async (symbol, timeframe = 'H1') => {
    if (!symbol || typeof symbol !== 'string') {
        throw new Error('Invalid symbol parameter');
    }

    const url = `${DATAFEED_BASE_URL}/betaspread/percentiles?symbol=${encodeURIComponent(symbol)}&timeframe=${timeframe}`;

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`DataFeed API error: ${response.status}`);
        }

        const data = await response.json();

        console.log('[DataFeed API] Percentile bands updated:', {
            P25: data.p25,
            P50: data.p50,
            P75: data.p75,
            regime: data.volatilityRegime
        });

        return data;

    } catch (error) {
        console.error('[DataFeed API] fetchPercentileBands failed:', error.message);
        throw error;
    }
};

/**
 * Fetch list of available trading pairs
 *
 * Returns all symbol pairs available in the DataFeed system.
 *
 * @returns {Promise<Array<string>>} Array of symbol pairs
 *
 * @example
 * const symbols = await fetchAvailableSymbols();
 * // ["XAUUSD/XAUEUR", "EURUSD/GBPUSD", ...]
 */
export const fetchAvailableSymbols = async () => {
    const url = `${DATAFEED_BASE_URL}/betaspread/symbols`;

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`DataFeed API error: ${response.status}`);
        }

        const data = await response.json();
        return Array.isArray(data) ? data : [];

    } catch (error) {
        console.error('[DataFeed API] fetchAvailableSymbols failed:', error.message);
        return [];
    }
};

/**
 * TypeScript-style type definitions (for JSDoc reference)
 *
 * @typedef {Object} StrategyDataResponse
 * @property {string} symbol - Trading pair
 * @property {string} timeframe - Timeframe (M1/M15/H1/H4/D1)
 * @property {number} count - Number of bars returned
 * @property {string} queryTimestamp - ISO 8601 timestamp
 * @property {Array<StrategyDataBar>} data - Historical bars
 * @property {PercentileBands} percentiles - Percentile bands (if requested)
 */

/**
 * @typedef {Object} StrategyDataBar
 * @property {string} timestamp - ISO 8601 timestamp
 * @property {number} spread - Beta spread value
 * @property {number} [beta] - Beta coefficient (nullable)
 * @property {number} [correlation] - Correlation value (nullable)
 * @property {number} [zScore] - Z-Score value (nullable)
 * @property {string} [session] - Trading session (ASIAN/LONDON/NY/OVERLAP)
 */

/**
 * @typedef {Object} PercentileBands
 * @property {number} p1 - 1st percentile
 * @property {number} p5 - 5th percentile
 * @property {number} p10 - 10th percentile
 * @property {number} p25 - 25th percentile (Q1, buy threshold)
 * @property {number} p50 - 50th percentile (median)
 * @property {number} p75 - 75th percentile (Q3, sell threshold)
 * @property {number} p90 - 90th percentile
 * @property {number} p95 - 95th percentile
 * @property {number} p99 - 99th percentile
 * @property {number} dailyHigh - Daily high value
 * @property {number} dailyLow - Daily low value
 * @property {number} dailyRange - Daily range (high - low)
 * @property {number} adjustedUpper - Adjusted upper bound
 * @property {number} adjustedLower - Adjusted lower bound
 * @property {string} session - Current trading session
 * @property {string} volatilityRegime - Volatility regime (NORMAL/HIGH/EXTREME)
 * @property {string} calculatedAt - ISO 8601 timestamp when calculated
 */
